import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, writeBatch, doc, getDoc, setDoc } from 'firebase/firestore';
import { db, getFCMToken } from '../firebase/firebaseConfig';
import { useAuth } from '../firebase/useAuth';
import { localHourToUTC, PRESETS } from '../firebase/notifications';
import styles from './SettingsSheet.module.css';

export default function SettingsSheet({ onClose }) {
  const { user, logout } = useAuth();
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('morning');
  const [permissionError, setPermissionError] = useState(null); // null | string
  const [saving, setSaving] = useState(false);
  const [tokenRegistered, setTokenRegistered] = useState(false);

  const settingsDocRef = useMemo(
    () => doc(db, 'users', user.uid, 'settings', 'creativity'),
    [user.uid]
  );

  useEffect(() => {
    getDoc(settingsDocRef).then((snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data.notificationsEnabled) setNotifEnabled(true);
      if (data.fcmToken) setTokenRegistered(true);
      if (data.promptHourUTC !== undefined) {
        const offsetHours = new Date().getTimezoneOffset() / 60;
        const localHour = (data.promptHourUTC - offsetHours + 24) % 24;
        const nearest = PRESETS.reduce((a, b) =>
          Math.abs(b.localHour - localHour) < Math.abs(a.localHour - localHour) ? b : a
        );
        setSelectedPreset(nearest.key);
      }
    }).catch(() => {});
  }, [settingsDocRef]);

  const handleToggle = async () => {
    if (notifEnabled) {
      setNotifEnabled(false);
      setTokenRegistered(false);
      await setDoc(settingsDocRef, { notificationsEnabled: false }, { merge: true });
      return;
    }
    setSaving(true);
    setPermissionError(null);
    try {
      if (!('Notification' in window)) {
        setPermissionError('This browser does not support notifications.');
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission === 'denied') {
        setPermissionError('Permission denied — open your browser/OS settings and allow notifications for this site, then try again.');
        return;
      }
      if (permission !== 'granted') {
        setPermissionError('Permission not granted. Try again.');
        return;
      }
      const token = await getFCMToken();
      if (!token) {
        setPermissionError('Could not get a push token. Make sure REACT_APP_FCM_VAPID_KEY is set and the app is served over HTTPS.');
        return;
      }
      const preset = PRESETS.find(p => p.key === selectedPreset) || PRESETS[0];
      const promptHourUTC = localHourToUTC(preset.localHour);
      await setDoc(settingsDocRef, {
        notificationsEnabled: true,
        promptHourUTC,
        fcmToken: token,
      }, { merge: true });
      setNotifEnabled(true);
      setTokenRegistered(true);
    } catch (err) {
      console.warn('Notification enable error:', err);
      setPermissionError(`Error: ${err.message || 'Unknown error'}. Check the console for details.`);
    } finally {
      setSaving(false);
    }
  };

  const handlePresetSelect = async (preset) => {
    setSelectedPreset(preset.key);
    if (!notifEnabled) return;
    await setDoc(settingsDocRef, {
      promptHourUTC: localHourToUTC(preset.localHour),
    }, { merge: true });
  };

  const handleSignOut = async () => {
    await logout();
    onClose();
  };

  const handleClearData = async () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setClearing(true);
    try {
      const entriesSnap = await getDocs(collection(db, 'users', user.uid, 'entries'));
      const batch = writeBatch(db);
      entriesSnap.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
    } catch (err) {
      console.warn('Clear data error:', err);
    }
    setClearing(false);
    setConfirmClear(false);
    onClose();
  };

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.sheet}>
        <div className={styles.handle} />

        <div className={styles.userRow}>
          <span className={styles.label}>Signed in as</span>
          <span className={styles.email}>{user?.email || user?.displayName}</span>
        </div>

        <div className={styles.divider} />

        <div className={styles.notifRow}>
          <div className={styles.notifInfo}>
            <span className={styles.itemIcon}>🔔</span>
            <div>
              <div className={styles.notifLabel}>Daily reminder</div>
              <div className={styles.notifSub}>Prompt to fill in today's entries</div>
            </div>
          </div>
          <button
            className={`${styles.toggle} ${notifEnabled ? styles.toggleOn : ''}`}
            onClick={handleToggle}
            disabled={saving}
            aria-label="Toggle daily reminder"
          >
            <span className={styles.toggleThumb} />
          </button>
        </div>

        {permissionError && (
          <p className={styles.permissionError}>{permissionError}</p>
        )}

        {notifEnabled && tokenRegistered && (
          <p className={styles.tokenStatus}>✓ Push token registered</p>
        )}
        {notifEnabled && !tokenRegistered && !saving && (
          <p className={styles.permissionError}>⚠ Token missing — toggle off and back on to re-register.</p>
        )}

        {notifEnabled && (
          <div className={styles.presetSection}>
            <span className={styles.presetLabel}>Send at</span>
            <div className={styles.chips}>
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  className={`${styles.chip} ${selectedPreset === p.key ? styles.chipSelected : ''}`}
                  onClick={() => handlePresetSelect(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <span className={styles.tzNote}>Times are in your local timezone</span>
          </div>
        )}

        <div className={styles.divider} />

        <button className={styles.item} onClick={handleSignOut}>
          <span className={styles.itemIcon}>🚪</span>
          <span>Sign out</span>
        </button>

        <button
          className={`${styles.item} ${styles.destructive}`}
          onClick={handleClearData}
          disabled={clearing}
        >
          <span className={styles.itemIcon}>🗑️</span>
          <span>
            {clearing ? 'Clearing…' : confirmClear ? 'Tap again to confirm' : 'Clear my data'}
          </span>
        </button>
      </div>
    </>
  );
}
